import {
  ArrowRight,
  Bot,
  Check,
  CircleAlert,
  FileCheck2,
  FileText,
  Image as ImageIcon,
  Layers3,
  Mic2,
  Save,
  ShieldCheck,
  Sparkles,
  Upload,
  UserRound,
  Video,
  WandSparkles,
  Waypoints,
} from 'lucide-react';
import { useTeamBrand } from '../branding/TeamBrandContext';

const Node = ({ icon: Icon, label, tone = 'lilac' }) => (
  <div className={`flex min-w-0 flex-1 flex-col items-center justify-center rounded-[18px] border px-3 py-4 text-center shadow-sm help-visual-node help-visual-node--${tone}`}>
    <span className="grid h-9 w-9 place-items-center rounded-[13px] bg-white/72 shadow-sm dark:bg-white/[0.08]"><Icon size={17} /></span>
    <span className="mt-2 text-[14px] font-semibold">{label}</span>
  </div>
);

const Arrow = () => <ArrowRight className="shrink-0 text-[#9d90ac] dark:text-white/30" size={16} />;

export default function HelpVisual({ type }) {
  const brand = useTeamBrand();
  if (type === 'idea') {
    return (
      <div className="help-visual help-visual--idea">
        <div className="help-visual-prompt"><Sparkles size={15} /><span>一个替姐姐出嫁的女孩，发现豪门藏着姐姐死亡的秘密……</span></div>
        <div className="help-visual-action"><WandSparkles size={15} />从这个想法开始</div>
      </div>
    );
  }

  if (type === 'pipeline') {
    return (
      <div className="help-visual">
        <div className="flex items-center gap-2">
          <Node icon={Sparkles} label="创意" /> <Arrow />
          <Node icon={Layers3} label="大纲" tone="sage" /> <Arrow />
          <Node icon={FileText} label="正文" tone="peach" /> <Arrow />
          <Node icon={FileCheck2} label="审核" tone="blue" />
        </div>
      </div>
    );
  }

  if (['review', 'facts', 'score', 'report'].includes(type)) {
    return (
      <div className="help-visual grid gap-3 sm:grid-cols-[1fr_.8fr]">
        <div className="rounded-[18px] border border-black/[0.07] bg-white/72 p-4 dark:border-white/[0.08] dark:bg-white/[0.04]">
          <div className="flex items-center gap-2 text-[14px] font-semibold"><ShieldCheck size={16} className="text-[#7f7095]" />连续性检查</div>
          <div className="mt-3 space-y-2 text-[14px] text-stone-500 dark:text-white/50">
            <div className="flex items-center gap-2 rounded-xl bg-[#e8f0e9] px-3 py-2 text-[#587166] dark:bg-emerald-400/10 dark:text-emerald-200"><Check size={12} />人物动机清楚</div>
            <div className="flex items-center gap-2 rounded-xl bg-[#f3e8e4] px-3 py-2 text-[#946c62] dark:bg-rose-400/10 dark:text-rose-200"><CircleAlert size={12} />第 8 集提前知道秘密</div>
          </div>
        </div>
        <div className="grid place-items-center rounded-[18px] bg-[#332d39] p-4 text-white dark:bg-white/[0.07]">
          <div className="text-center"><strong className="block text-[38px] leading-none">A</strong><span className="mt-2 block text-[14px] text-white/60">值得优化</span></div>
        </div>
      </div>
    );
  }

  if (['upload', 'document'].includes(type)) {
    return (
      <div className="help-visual flex items-center gap-3">
        <Node icon={Upload} label="粘贴 / 上传" /> <Arrow />
        <Node icon={FileText} label="提取正文" tone="blue" /> <Arrow />
        <Node icon={FileCheck2} label="审核报告" tone="sage" />
      </div>
    );
  }

  if (['agent', 'context'].includes(type)) {
    return (
      <div className="help-visual">
        <div className="ml-auto max-w-[78%] rounded-[18px_18px_5px_18px] bg-[#ded5e8] px-4 py-3 text-[14px] leading-6 text-[#473d50] dark:bg-[#6d5c82]/45 dark:text-white/80">只改第 12 集审讯场，保留人物不知道遗书的事实。</div>
        <div className="mt-3 flex max-w-[88%] gap-2 rounded-[18px_18px_18px_5px] border border-black/[0.07] bg-white/74 px-4 py-3 text-[14px] leading-6 text-stone-600 dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-white/60"><Bot size={17} className="mt-0.5 shrink-0 text-[#83739a]" />收到。我只调整冲突进入时机，不新增角色，也不动结尾钩子。</div>
      </div>
    );
  }

  if (['canvas', 'nodes'].includes(type)) {
    return (
      <div className="help-visual flex items-center gap-3">
        <Node icon={FileText} label="场景文字" /> <Arrow />
        <Node icon={ImageIcon} label="首帧图片" tone="sage" /> <Arrow />
        <Node icon={Video} label="视频片段" tone="blue" />
      </div>
    );
  }

  if (['character', 'character-grid'].includes(type)) {
    return (
      <div className="help-visual grid grid-cols-[.8fr_1.2fr] gap-3">
        <div className="grid place-items-center rounded-[20px] bg-gradient-to-br from-[#d9cde7] via-[#e8ded9] to-[#cbd8cf] text-[#5f526d]"><UserRound size={42} strokeWidth={1.2} /></div>
        <div className="grid grid-cols-2 gap-2">
          {['正面', '45°', '半身', '全身'].map(label => <div key={label} className="grid min-h-16 place-items-center rounded-[14px] border border-white/60 bg-white/54 text-[14px] font-medium text-stone-500 shadow-sm dark:border-white/[0.06] dark:bg-white/[0.04] dark:text-white/50">{label}</div>)}
        </div>
      </div>
    );
  }

  if (['scene', 'references'].includes(type)) {
    return (
      <div className="help-visual relative overflow-hidden bg-gradient-to-br from-[#25323d] via-[#39414d] to-[#7c6b73] text-white">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_22%_28%,#d7e6ee_0,transparent_28%),radial-gradient(circle_at_78%_72%,#d8bfa4_0,transparent_30%)]" />
        <div className="relative flex min-h-[116px] items-end justify-between">
          <div><span className="text-[14px] tracking-[.08em] text-white/60">场景参考</span><strong className="mt-2 block text-[18px]">雨夜 · 旧照相馆</strong></div>
          <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[14px]">35mm 广角</span>
        </div>
      </div>
    );
  }

  if (type === 'editor') {
    return (
      <div className="help-visual grid grid-cols-[.65fr_1.45fr_.9fr] gap-2">
        <div className="rounded-[16px] bg-[#ede8e2] p-3 dark:bg-white/[0.035]"><span className="text-[14px] font-semibold text-stone-400">故事结构</span><div className="mt-3 space-y-2">{[1, 2, 3].map(n => <div key={n} className="h-7 rounded-lg bg-white/68 dark:bg-white/[0.05]" />)}</div></div>
        <div className="rounded-[16px] bg-white/84 p-4 shadow-sm dark:bg-white/[0.055]"><span className="text-[14px] text-stone-400">第 12 集</span><div className="mt-3 h-2 w-3/4 rounded bg-stone-300/60 dark:bg-white/10" /><div className="mt-2 h-2 w-full rounded bg-stone-200/70 dark:bg-white/[0.06]" /><div className="mt-2 h-2 w-5/6 rounded bg-stone-200/70 dark:bg-white/[0.06]" /></div>
        <div className="rounded-[16px] bg-[#3a3440] p-3 text-white dark:bg-white/[0.055]"><span className="flex items-center gap-1.5 text-[14px]"><Bot size={15} />{brand.text('小影 Agent')}</span><div className="mt-3 rounded-lg bg-white/[0.07] p-2 text-[14px] leading-6 text-white/60">我在这儿，随时帮你改这场戏。</div></div>
      </div>
    );
  }

  if (type === 'save') {
    return <div className="help-visual flex min-h-[120px] items-center justify-center"><div className="flex items-center gap-3 rounded-full border border-black/[0.07] bg-white/76 px-5 py-3 text-[14px] font-medium text-stone-600 shadow-sm dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-white/60"><Save size={17} className="text-[#7c6d91]" /><span>已保存 · 刚刚</span><Check size={15} className="text-[#6d897b]" /></div></div>;
  }

  if (['video', 'audio'].includes(type)) {
    return (
      <div className="help-visual flex items-center gap-3">
        <Node icon={ImageIcon} label="角色首帧" /> <Arrow />
        <Node icon={type === 'audio' ? Mic2 : Video} label={type === 'audio' ? '参考声线' : '动作镜头'} tone="peach" /> <Arrow />
        <Node icon={Video} label="有声片段" tone="blue" />
      </div>
    );
  }

  if (['assets', 'delivery'].includes(type)) {
    return (
      <div className="help-visual flex items-center gap-3">
        <Node icon={Layers3} label="角色 / 场景" /> <Arrow />
        <Node icon={Waypoints} label="镜序清单" tone="sage" /> <Arrow />
        <Node icon={FileCheck2} label="交付版本" tone="blue" />
      </div>
    );
  }

  if (type === 'human') {
    return <div className="help-visual flex min-h-[126px] items-center justify-center gap-4"><Node icon={Bot} label="AI 初审" /><Arrow /><Node icon={UserRound} label="真人复核" tone="sage" /><Arrow /><Node icon={FileCheck2} label="审核意见" tone="blue" /></div>;
  }

  if (type === 'shield') {
    return <div className="help-visual flex min-h-[124px] items-center justify-center"><div className="grid h-20 w-20 place-items-center rounded-full bg-[#dfe9e3] text-[#587166] shadow-[0_18px_40px_-28px_rgba(52,85,68,.65)] dark:bg-emerald-400/10 dark:text-emerald-200"><ShieldCheck size={34} strokeWidth={1.4} /></div></div>;
  }

  if (type === 'trouble') {
    return <div className="help-visual flex min-h-[124px] items-center justify-center"><div className="flex items-center gap-3 rounded-[18px] bg-[#f1e3df] px-5 py-4 text-[14px] text-[#855f58] dark:bg-rose-400/10 dark:text-rose-200"><CircleAlert size={18} /><span>这个节点需要检查参考图或重新生成</span></div></div>;
  }

  return null;
}
